/**
 * AuthPage — email OTP login (phone is optional, not verified).
 *
 * Email-first because the project doesn't have a Twilio account yet —
 * Supabase Auth's email provider works out of the box (built-in sender
 * for low volume, plug in custom SMTP later). When Twilio is wired up,
 * we'll add a "Verify by phone instead" option back in.
 *
 * Two steps:
 *   1. Email + (optional) phone entry
 *   2. 6-digit code entry — verified via supabase.auth.verifyOtp({ type: 'email' })
 *
 * After verify: bootstrap_customer_profile RPC upserts the `customers`
 * row with the email + phone under the current tenant.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Container, Stack, TextField, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { supabase } from '../supabase';
import { TENANT_ID } from '../config';
import { useAuth } from '../context/AuthContext';

const OTP_LEN = 6;

export const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshProfile } = useAuth();

  const [step, setStep] = useState<'entry' | 'otp'>('entry');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState<string | undefined>(undefined);
  const [name, setName] = useState('');
  const [otp, setOtp] = useState<string[]>(Array(OTP_LEN).fill(''));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const otpRefs = useRef<Array<HTMLInputElement | null>>([]);
  const returnTo = (location.state as any)?.from || '/checkout';

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  useEffect(() => {
    if (step === 'otp') setTimeout(() => otpRefs.current[0]?.focus(), 100);
  }, [step]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const sendOtp = async () => {
    if (!emailValid) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { error: err } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          // Allow creating a new user on first verification.
          shouldCreateUser: true,
        },
      });
      if (err) throw err;
      setStep('otp');
      setCountdown(60);
      setOtp(Array(OTP_LEN).fill(''));
    } catch (err: any) {
      console.error('[auth] sendOtp(email) failed:', err);
      const msg = String(err?.message || '').toLowerCase();
      if (msg.includes('rate'))           setError('Too many requests. Try again in a minute.');
      else if (msg.includes('invalid'))   setError('Email rejected. Please double-check.');
      else                                 setError('Failed to send code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (code: string) => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase.auth.verifyOtp({
        email: email.trim(),
        token: code,
        type: 'email',
      });
      if (err) throw err;
      if (!data.session) throw new Error('No session returned');

      // Persist optional phone + name on the customers row.
      await supabase.rpc('bootstrap_customer_profile', {
        p_tenant_id: TENANT_ID,
        p_full_name: name || null,
        p_phone:     phone || null,
      });
      await refreshProfile();

      // If the customer doesn't have a saved delivery address yet, route
      // them through /complete-profile first. Otherwise go straight to
      // wherever they were headed.
      const { data: profileRow } = await supabase.rpc('current_customer_profile');
      const p: any = Array.isArray(profileRow) ? profileRow[0] : profileRow;
      const needsCompletion = !p?.full_name || !p?.address_line;
      navigate(needsCompletion ? '/complete-profile' : returnTo, {
        replace: true,
        state: { from: returnTo },
      });
    } catch (err: any) {
      console.error('[auth] verifyOtp(email) failed:', err);
      const msg = String(err?.message || '').toLowerCase();
      if (msg.includes('expired'))      setError('Code expired. Tap Resend.');
      else if (msg.includes('invalid')) setError('Invalid code. Try again.');
      else                              setError('Verification failed.');
      setOtp(Array(OTP_LEN).fill(''));
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (i: number, raw: string) => {
    let v = raw.length > 1 ? raw.slice(-1) : raw;
    if (v && !/^\d$/.test(v)) return;
    const next = [...otp];
    next[i] = v;
    setOtp(next);
    if (v && i < OTP_LEN - 1) otpRefs.current[i + 1]?.focus();
    if (v && i === OTP_LEN - 1 && next.every((d) => d !== '')) verifyOtp(next.join(''));
  };

  const handleOtpKey = (i: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const p = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LEN);
    if (p.length === OTP_LEN) {
      setOtp(p.split(''));
      otpRefs.current[OTP_LEN - 1]?.focus();
      verifyOtp(p);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ pt: 4, pb: 8 }}>
      <Box
        sx={{
          backgroundColor: 'rgba(10,37,64,0.6)',
          border: '1px solid rgba(0,200,150,0.2)',
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          color: '#fff',
        }}
      >
        <Stack spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
          <Box
            component="img"
            src="/saheeh_logo_w.svg"
            alt="Saheeh"
            sx={{ height: 56 }}
          />
          <Typography sx={{ fontSize: '22px', fontWeight: 800, color: '#fff' }}>
            {step === 'entry' ? 'Welcome' : 'Enter Verification Code'}
          </Typography>
          <Typography
            sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', textAlign: 'center', maxWidth: 360 }}
          >
            {step === 'entry'
              ? 'Sign in with your email to place your order. We’ll send you a 6-digit code.'
              : `Code sent to ${email}`}
          </Typography>
        </Stack>

        {step === 'entry' && (
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Email"
              value={email}
              type="email"
              autoComplete="email"
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{
                '& .MuiInputBase-root': { backgroundColor: 'rgba(255,255,255,0.06)', color: '#fff' },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00C896 !important' },
              }}
            />

            <TextField
              fullWidth
              label="Full name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{
                '& .MuiInputBase-root': { backgroundColor: 'rgba(255,255,255,0.06)', color: '#fff' },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00C896 !important' },
              }}
            />

            <Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '12px', mb: 0.75 }}>
                PHONE NUMBER (OPTIONAL)
              </Typography>
              <PhoneInput
                international
                defaultCountry="SA"
                value={phone}
                onChange={setPhone}
                placeholder="5xxxxxxxx"
              />
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', mt: 0.75 }}>
                We won’t verify it now — it’s only used so the restaurant can call you about your order.
              </Typography>
              <style>{`
                .PhoneInput { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.15); border-radius: 10px; padding: 10px 14px; display: flex; align-items: center; gap: 10px; }
                .PhoneInput:focus-within { border-color: #00C896; }
                .PhoneInputCountry { display: flex; align-items: center; gap: 4px; }
                .PhoneInputCountryIcon { width: 24px; height: 18px; border-radius: 2px; overflow: hidden; }
                .PhoneInputCountrySelectArrow { border-color: rgba(255,255,255,0.6); opacity: 0.7; margin-left: 4px; }
                .PhoneInputCountrySelect { background: #0A2540; color: #fff; }
                .PhoneInputInput { background: transparent !important; border: none !important; color: #fff !important; font-size: 1.05rem !important; outline: none !important; flex: 1; letter-spacing: 0.5px; }
                .PhoneInputInput::placeholder { color: rgba(255,255,255,0.4) !important; }
              `}</style>
            </Box>

            {error && (
              <Typography sx={{ color: '#ef5350', fontSize: '13px', textAlign: 'center' }}>{error}</Typography>
            )}

            <Button
              onClick={sendOtp}
              disabled={loading || !emailValid}
              sx={{
                py: 1.25,
                background: 'linear-gradient(135deg, #00C896 0%, #00A37A 100%)',
                color: '#0A2540',
                fontWeight: 700,
                fontSize: '15px',
                '&:hover': { background: 'linear-gradient(135deg, #1AD4A6 0%, #00B387 100%)' },
                '&:disabled': { opacity: 0.5, color: 'rgba(10,37,64,0.7)' },
              }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: '#0A2540' }} /> : 'Send Code'}
            </Button>

            <Button
              onClick={() => navigate(returnTo, { replace: true })}
              sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontSize: '13px' }}
            >
              Continue without signing in
            </Button>
          </Stack>
        )}

        {step === 'otp' && (
          <Stack spacing={2.5} alignItems="center">
            <Button
              onClick={() => { setStep('entry'); setError(''); }}
              sx={{ color: '#00C896', textTransform: 'none', fontSize: '13px' }}
            >
              ← Change email
            </Button>

            <Stack direction="row" spacing={1.25}>
              {otp.map((digit, i) => (
                <Box
                  key={i}
                  component="input"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  ref={(el: HTMLInputElement) => { otpRefs.current[i] = el; }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent) => handleOtpKey(i, e)}
                  onPaste={i === 0 ? handleOtpPaste : undefined}
                  sx={{
                    width: 46, height: 56, textAlign: 'center', fontSize: '1.4rem', fontWeight: 700,
                    borderRadius: '10px', border: `2px solid ${digit ? '#00C896' : 'rgba(255,255,255,0.2)'}`,
                    background: 'rgba(255,255,255,0.06)', color: '#fff', outline: 'none',
                    transition: 'border-color 0.2s',
                    '&:focus': { borderColor: '#00C896' },
                  }}
                />
              ))}
            </Stack>

            {error && (
              <Typography sx={{ color: '#ef5350', fontSize: '13px', textAlign: 'center' }}>{error}</Typography>
            )}
            {loading && (
              <Typography sx={{ color: '#00C896', fontSize: '13px' }}>Verifying...</Typography>
            )}

            <Button
              onClick={sendOtp}
              disabled={countdown > 0 || loading}
              sx={{
                color: countdown > 0 ? 'rgba(255,255,255,0.4)' : '#00C896',
                textTransform: 'none',
                fontSize: '13px',
              }}
            >
              {countdown > 0 ? `Resend code in ${countdown}s` : 'Resend code'}
            </Button>
          </Stack>
        )}
      </Box>
    </Container>
  );
};
